<sui-toolbar #tb>
  <sui-checkbox
    [checked]="selectAllChecked"
    (checkedChange)="onSelectAllChange($event)"
    [suiTooltip]="'widget-actions.button.selected-tooltip' | translate"
  ></sui-checkbox>

  <app-search-input
    [placeholder]="'widget-actions.placeholder.find-widget' | translate"
    (inputChanged)="searchChange.emit($event)"
  ></app-search-input>

  <sui-button
    variant="ghost"
    size="sm"
    [iconOnly]="true"
    [leadingIcon]="['fas', 'refresh']"
    [ariaLabel]="'widget-actions.button.refresh-tooltip' | translate"
    [suiTooltip]="'widget-actions.button.refresh-tooltip' | translate"
    (click)="refresh.emit()"
  ></sui-button>

  <!-- Selected (only when a selection exists) -->
  @if (selectedCount$ | async; as selectedCount) {
    @if (selectedCount > 0) {
      <sui-button
        variant="ghost"
        [leadingIcon]="['fas', 'check-circle']"
        [trailingIcon]="['fas', 'caret-down']"
        [iconOnly]="tb.collapsed()"
        [ariaLabel]="tb.collapsed() ? selectedLabel : undefined"
        [suiTooltip]="tb.collapsed() ? selectedLabel : ''"
        [suiPopoverTrigger]="selectedPanel"
        suiPopoverPlacement="bottom-start"
        #selectedTrigger="suiPopoverTrigger"
        [expanded]="selectedTrigger.isOpen()"
      >
        {{
          tb.collapsed()
            ? selectedCount
            : selectedLabel + " (" + selectedCount + ")"
        }}
      </sui-button>
      <ng-template #selectedPanel>
        <div suiPopover class="dropdown-menu">
          @if (tb.collapsed()) {
            <div class="dropdown-heading">
              {{ selectedLabel }}
            </div>
          }
          @for (item of selectedItems; track item) {
            @if (item.isSeparator) {
              <div class="dropdown-divider"></div>
            }
            @if (!item.isSeparator) {
              <a
                class="dropdown-item"
                (click)="
                  item.action?.();
                  $event.preventDefault();
                  $event.stopPropagation()
                "
              >
                <span class="dropdown-item-link-col1">
                  <span class="dropdown-item-link-label">
                    @if (item.icon) {
                      <div>
                        <fa-icon [icon]="item.icon"></fa-icon>
                      </div>
                    }
                    <span>{{ item.text }}</span>
                  </span>
                </span>
              </a>
            }
          }
        </div>
      </ng-template>
    }
  }

  <!-- Page preview: dashboard only (first of the right-aligned group) -->
  @if (isDashboard) {
    <sui-button
      class="right-group-start"
      variant="ghost"
      size="sm"
      [leadingIcon]="['fas', 'chalkboard-user']"
      [iconOnly]="tb.collapsed()"
      [ariaLabel]="
        tb.collapsed()
          ? ('presentation-actions.button.page-preview' | translate)
          : undefined
      "
      [suiTooltip]="
        tb.collapsed()
          ? ('presentation-actions.button.page-preview' | translate)
          : ''
      "
      (click)="onPagePreview()"
    >
      {{
        tb.collapsed()
          ? ""
          : ("presentation-actions.button.page-preview" | translate)
      }}
    </sui-button>
  }

  <!-- Actions -->
  <sui-button
    class="right-group-start"
    variant="ghost"
    [leadingIcon]="['fas', 'bars']"
    [trailingIcon]="['fas', 'caret-down']"
    [iconOnly]="tb.collapsed()"
    [ariaLabel]="tb.collapsed() ? actionsLabel : undefined"
    [suiTooltip]="
      tb.collapsed()
        ? actionsLabel
        : ('widget-actions.button.actions-tooltip' | translate)
    "
    [suiPopoverTrigger]="actionsPanel"
    suiPopoverPlacement="bottom-end"
    #actionsTrigger="suiPopoverTrigger"
    [expanded]="actionsTrigger.isOpen()"
  >
    {{ tb.collapsed() ? "" : actionsLabel }}
  </sui-button>
  <ng-template #actionsPanel>
    <div suiPopover class="dropdown-menu">
      @if (tb.collapsed()) {
        <div class="dropdown-heading">
          {{ actionsLabel }}
        </div>
      }
      @for (item of actionItems; track item) {
        @if (item.isSeparator) {
          <div class="dropdown-divider"></div>
        }
        @if (!item.isSeparator) {
          <a
            class="dropdown-item"
            (click)="
              item.action?.(); $event.preventDefault(); $event.stopPropagation()
            "
          >
            <span class="dropdown-item-link-col1">
              <span class="dropdown-item-link-label">
                @if (item.icon) {
                  <div>
                    <fa-icon [icon]="item.icon"></fa-icon>
                  </div>
                }
                <span>{{ item.text }}</span>
              </span>
            </span>
          </a>
        }
      }
    </div>
  </ng-template>

  <!-- View -->
  <sui-button
    variant="ghost"
    [leadingIcon]="['fas', 'eye']"
    [trailingIcon]="['fas', 'caret-down']"
    [iconOnly]="tb.collapsed()"
    [ariaLabel]="tb.collapsed() ? viewLabel : undefined"
    [suiTooltip]="
      tb.collapsed()
        ? viewLabel
        : ('widget-actions.button.view-tooltip' | translate)
    "
    [suiPopoverTrigger]="viewPanel"
    suiPopoverPlacement="bottom-end"
    #viewTrigger="suiPopoverTrigger"
    [expanded]="viewTrigger.isOpen()"
  >
    {{ tb.collapsed() ? "" : viewLabel }}
  </sui-button>
  <ng-template #viewPanel>
    <div suiPopover class="dropdown-menu">
      @if (tb.collapsed()) {
        <div class="dropdown-heading">{{ viewLabel }}</div>
      }
      @for (item of viewItems$ | async; track item) {
        <sui-checkbox
          class="dropdown-item"
          indicatorPosition="trailing"
          [checked]="!!item.isSelected"
          (checkedChange)="item.action?.()"
        >
          @if (item.icon) {
            <fa-icon [icon]="item.icon"></fa-icon>
          }
          <span>{{ item.text }}</span>
        </sui-checkbox>
      }
    </div>
  </ng-template>

  <!-- Languages -->
  <sui-button
    variant="ghost"
    [leadingIcon]="['fas', 'language']"
    [trailingIcon]="['fas', 'caret-down']"
    [iconOnly]="tb.collapsed()"
    [ariaLabel]="tb.collapsed() ? languagesLabel : undefined"
    [suiTooltip]="
      tb.collapsed()
        ? languagesLabel
        : ('widget-actions.button.language-tooltip' | translate)
    "
    [suiPopoverTrigger]="languagePanel"
    suiPopoverPlacement="bottom-end"
    #languageTrigger="suiPopoverTrigger"
    [expanded]="languageTrigger.isOpen()"
  >
    {{ tb.collapsed() ? "" : languagesLabel }}
  </sui-button>
  <ng-template #languagePanel>
    <div suiPopover class="dropdown-menu">
      @if (tb.collapsed()) {
        <div class="dropdown-heading">
          {{ languagesLabel }}
        </div>
      }
      <!-- Language is a single mutually exclusive set (see WidgetSettingsService). -->
      @if (languageItems$ | async; as languageItems) {
        <div
          sui-radio-group
          orientation="vertical"
          [ngModel]="selectedValueOf(languageItems)"
          [ngModelOptions]="{ standalone: true }"
          (valueChange)="onLanguageChange(languageItems, $event)"
        >
          @for (item of languageItems; track item) {
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              [value]="$any(item.value)"
            >
              @if (item.icon) {
                <fa-icon [icon]="item.icon"></fa-icon>
              }
              <span>{{ item.text }}</span>
            </sui-radio>
          }
        </div>
      }
    </div>
  </ng-template>

  <!-- Configure: feed only -->
  @if (!isDashboard) {
    <sui-button
      variant="ghost"
      [iconOnly]="true"
      [leadingIcon]="['fas', 'gear']"
      [ariaLabel]="
        'widget-actions.button.tooltip.compact-view-to-reorder-widgets'
          | translate
      "
      [suiTooltip]="
        'widget-actions.button.tooltip.compact-view-to-reorder-widgets'
          | translate
      "
      (click)="configure.emit()"
    ></sui-button>
  }
</sui-toolbar>
